'use client'

import { useTranslations } from 'next-intl'
import { Button } from '@/shared/ui/button/button'
import { motion } from 'framer-motion'
import { AnimatedTitle } from '@/shared/ui/animated-title/animated-title'
import { AnimatedSlogan } from '@/shared/ui/animated-slogan/animated-slogan'

type FormType = 'buy' | 'sell'

interface HeroProps {
  onFormTypeChange: (type: FormType) => void
  activeForm: FormType
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
}

export const Hero = ({ onFormTypeChange, activeForm }: HeroProps) => {
  const t = useTranslations('hero')

  const handleButtonClick = (type: FormType) => {
    onFormTypeChange(type)
    const formElement = document.getElementById('form-section')
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <section className="container flex flex-col items-center justify-center gap-4 pb-6 pt-4 md:pb-8 md:pt-6">
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mx-auto flex max-w-[980px] flex-col items-center gap-4 md:gap-6"
      >
        <AnimatedTitle
          text={t('title')}
          tag="h1"
          className="text-2xl md:text-3xl lg:text-6xl font-bold leading-tight tracking-tighter text-center lg:leading-[1.1] whitespace-pre-wrap break-words"
        />
        <motion.div
          variants={item}
          className="flex flex-col items-center gap-3 md:gap-4 text-center px-4 md:px-0"
        >
          <motion.p
            variants={item}
            className="text-lg md:text-xl font-semibold text-orange-500 max-w-full break-words whitespace-normal"
          >
            <AnimatedSlogan
              fullText="Продадим ваше ООО - быстро в любом регионе!"
              changeableWords={['ваше', 'вам']}
              changeableWordIndex={1}
              className="inline-block"
            />
          </motion.p>
          <motion.div
            variants={item}
            className="text-base md:text-lg text-muted-foreground space-y-2"
          >
            <motion.p variants={item} className="font-medium">
              {t('description').split('\n\n')[1].split(':\n')[0]}:
            </motion.p>
            <motion.ul variants={container} className="list-none space-y-1">
              {t('description')
                .split('\n\n')[1]
                .split(':\n')[1]
                .split('\n')
                .map((listItem, index) => (
                  <motion.li key={index} variants={item} className="font-normal">
                    {listItem}
                  </motion.li>
                ))}
            </motion.ul>
          </motion.div>
          <motion.p variants={item} className="text-base md:text-lg font-medium mt-2">
            {t('description').split('\n\n')[2]}
          </motion.p>
          <motion.div
            variants={item}
            className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mt-2"
          >
            <Button
              onClick={() => handleButtonClick('sell')}
              className={`w-full sm:w-auto px-8 h-11 ${
                activeForm === 'sell' ? 'bg-orange-500 hover:bg-orange-600' : ''
              }`}
            >
              {t('sellCompany')}
            </Button>
            <Button
              onClick={() => handleButtonClick('buy')}
              className={`w-full sm:w-auto px-8 h-11 ${
                activeForm === 'buy' ? 'bg-orange-500 hover:bg-orange-600' : ''
              }`}
            >
              {t('findCompany')}
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  )
}
